import { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { setModal } from 'slices/modalSlice'
import { formatearFecha, validarRangoFechas } from '../../helpers/dateValidation.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import {
  buscarListaPorVendedor,
  actualizar as actualizarLista,
  insertarLista
} from '../servicios/listaVentaService.js'
import { BuscarPublicacionesPorLista } from '../servicios/publicacionesService'
import { ItemLista } from './ItemLista'
import { ListaContenido } from './ListaContenido'
import styles from './ListaVenta.module.css'
import { ModalEditarItemsLista } from './ModalEditarItemsLista'
import { useListaFiltros } from './useListaFiltros'

const campos = [
  {
    type: 'text',
    name: 'nombre',
    placeholder: 'Ingrese el nombre de la lista',
    label: 'Nombre',
    maxLength: 27,
    isFullWidth: true,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaDesde',
    label: 'Vigencia desde',
    isFullWidth: false,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaHasta',
    label: 'Vigencia hasta',
    isFullWidth: false,
    isEditable: true,
    isGrid: false
  }
]

const ESTADO_PUBLICADA = 2
const PAGINA_ITEMS = 15
const MAX_LENGTH_NOMBRE = 27

export const ListaVenta = () => {
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(PAGINA_ITEMS)
  const [listaVentas, setListaVentas] = useState([])
  const [listaVentasActual, setListaVentasActual] = useState([])
  const [showModalItems, setShowModalItems] = useState(false)
  const [listaSeleccionada, setListaSeleccionada] = useState(null)
  const topRef = useRef(null)
  const [actualizar, setActualizar] = useState(false)

  const dispatch = useDispatch()
  const { values, errores } = useSelector((state) => state.genericModal)
  const idVendedor = parseInt(localStorage.getItem('idUsuario'))

  const listaOrdenadaPorCantidad = useMemo(() => {
    return [...listaVentas].sort((a, b) => {
      const ca = a.cantidadItems ?? 0
      const cb = b.cantidadItems ?? 0
      return cb - ca
    })
  }, [listaVentas])

  const {
    listaFiltradaOrdenada,
    busqueda,
    setBusqueda,
    handleBusqueda,
    handleOrdenarAZ,
    handleOrdenarPorVencimiento
  } = useListaFiltros(listaOrdenadaPorCantidad, 'nombre')

  useEffect(() => {
    dispatch(setModal({ campos: campos }))
  }, [])

  const traerInfo = async () => {
    try {
      const { data } = await buscarListaPorVendedor(idVendedor)

      const contarItems = (lista) => {
        const countFields = [
          lista.cantidadItems,
          lista.cantItems,
          lista.totalItems,
          lista.totalPublicaciones,
          lista.total
        ]

        const hasCountField = countFields.some((v) => v !== undefined && v !== null)
        const num = Number(countFields.find((v) => v !== undefined && v !== null) ?? 0)

        const arrays = [lista.grilla, lista.items, lista.publicaciones]
        const arraysWithData = arrays.filter((a) => Array.isArray(a))
        const maxArray = arraysWithData.reduce((acc, arr) => Math.max(acc, arr.length), 0)
        const hasArray = arraysWithData.length > 0

        if (!hasCountField && !hasArray) {
          return null
        }

        return Math.max(num, maxArray)
      }

      const todosLosItems = (data || []).map((lista) => {
        const cantidadItems = contarItems(lista)
        return {
          nombre: lista.nombre,
          nombreLista: lista.nombre,
          vigenciaDesde: lista.vigenciaDesde,
          vigenciaHasta: lista.vigenciaHasta,
          ofertaDesde: lista.ofertaDesde,
          ofertaHasta: lista.ofertaHasta,
          porcDcto: lista.porcDcto,
          idListaVenta: lista.idListaVenta,
          idListaVentaEstado: lista.idListaVentaEstado,
          idVendedor: lista.idVendedor ?? idVendedor,
          signo: lista.signo,
          variacionPrecio: lista.variacionPrecio,
          variacionPrecioDesde: lista.variacionPrecioDesde,
          variacionPrecioHasta: lista.variacionPrecioHasta,
          cantidadItems
        }
      })

      const faltantes = todosLosItems.filter((l) => l.cantidadItems === null)
      if (faltantes.length > 0) {
        const conteos = await Promise.all(
          faltantes.map(async (l) => {
            const { data: pubs } = await BuscarPublicacionesPorLista(l.idListaVenta)
            const cant = Array.isArray(pubs) ? pubs.length : 0
            return { idListaVenta: l.idListaVenta, cantidadItems: cant }
          })
        )

        const conteosMap = new Map(conteos.map((c) => [c.idListaVenta, c.cantidadItems]))
        const ajustadas = todosLosItems.map((l) =>
          conteosMap.has(l.idListaVenta)
            ? { ...l, cantidadItems: conteosMap.get(l.idListaVenta) }
            : l
        )
        setListaVentas(ajustadas)
      } else {
        setListaVentas(todosLosItems)
      }
    } catch (error) {
      const mensaje = 'Ocurrió un error al cargar los items de venta.'
      toastErrorLog(error, mensaje, 'listaVenta')
    }
  }

  useEffect(() => {
    if (idVendedor) {
      traerInfo()
    }
  }, [idVendedor, actualizar])

  const validarNombreDuplicado = (nombre, editMode = false) => {
    if (!nombre || nombre.trim() === '') {
      return null
    }

    const nombreNormalizado = nombre.toLowerCase().trim()
    const existe = listaVentas.some((l) => {
      const mismoNombre = l.nombre.toLowerCase() === nombreNormalizado
      if (!editMode) {
        return mismoNombre
      }
      return mismoNombre && l.idListaVenta !== values.idListaVenta
    })

    return existe ? 'Ya existe una lista con ese nombre.' : null
  }

  const editarLista = (item) => {
    const fechaHoy = formatearFecha(new Date(), 'YYYY-MM-DD')

    dispatch(
      setModal({
        size: 'md',
        title: 'Editar lista de venta',
        type: 'form',
        body: '',
        show: true,
        values: {
          editMode: true,
          idListaVenta: item.idListaVenta,
          idVendedor: idVendedor,
          idListaVentaEstado: item.idListaVentaEstado || ESTADO_PUBLICADA,
          nombre: item.nombre,
          vigenciaDesde: fechaHoy,
          vigenciaHasta: formatearFecha(item.vigenciaHasta, 'YYYY-MM-DD'),
          ofertaDesde: item.ofertaDesde || null,
          ofertaHasta: item.ofertaHasta || null,
          porcDcto: item.porcDcto ?? null
        }
      })
    )
  }

  const handleVerItems = (lista) => {
    setListaSeleccionada(lista)
    setShowModalItems(true)
  }

  const handleActualizarItems = () => {
    setActualizar((prev) => !prev)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    const nuevasValidaciones = { ...errores }
    const valorFinal = name === 'nombre' ? String(value ?? '').slice(0, MAX_LENGTH_NOMBRE) : value
    dispatch(setModal({ values: { ...values, [name]: valorFinal } }))

    if (errores[name]) {
      delete nuevasValidaciones[name]
    }
    dispatch(setModal({ errores: nuevasValidaciones }))
  }

  const handleConfirmModal = async () => {
    const errores = {}

    const { nombre, vigenciaDesde, vigenciaHasta, ofertaDesde, ofertaHasta, porcDcto, editMode } =
      values

    if (!nombre || nombre.trim() === '') {
      errores.nombre = 'El nombre de la lista es obligatorio.'
    } else {
      const errorNombre = validarNombreDuplicado(nombre, editMode)
      if (errorNombre) {
        errores.nombre = errorNombre
      }
    }

    if (!editMode && !vigenciaDesde) {
      errores.vigenciaDesde = 'La fecha de inicio de vigencia es obligatoria.'
    } else if (vigenciaDesde) {
      const esFechaDesdeValida = validarRangoFechas(new Date(), vigenciaDesde, 'day')
      if (!esFechaDesdeValida) {
        errores.vigenciaDesde = 'La fecha de vigencia desde no puede ser menor a hoy'
      }
    }

    if (!editMode && !vigenciaHasta) {
      errores.vigenciaHasta = 'La fecha de fin de vigencia es obligatoria.'
    } else {
      const esFechaHastaValida = validarRangoFechas(vigenciaDesde, vigenciaHasta, 'day')
      if (!esFechaHastaValida) {
        errores.vigenciaHasta = 'La fecha de fin de vigencia debe ser mayor que la de inicio'
      }
    }

    if (ofertaDesde && ofertaHasta) {
      if (ofertaHasta <= ofertaDesde) {
        errores.ofertaHasta = 'La fecha de fin de oferta debe ser mayor que la de inicio.'
      }
    }

    if (porcDcto && (porcDcto <= 0 || porcDcto > 100)) {
      errores.porcDcto = 'El porcentaje debe estar entre 1 y 100.'
    }

    dispatch(
      setModal({
        errores: errores,
        type: 'form'
      })
    )

    if (Object.keys(errores).length > 0) {
      return false
    }

    try {
      if (editMode) {
        await handleActualizar()
      } else {
        await handleInsertar()
      }
      return true
    } catch (error) {
      const mensaje = editMode
        ? 'Ocurrió un error al actualizar la lista.'
        : 'Ocurrió un error al crear la lista.'
      toastErrorLog(error, mensaje, 'listaVenta')
      return false
    }
  }

  const abrirModal = () => {
    const fechaHoy = formatearFecha(new Date(), 'YYYY-MM-DD')

    dispatch(
      setModal({
        size: 'lg',
        title: 'Crear lista venta',
        type: 'form',
        body: '',
        show: true,
        values: {
          editMode: false,
          idVendedor: idVendedor,
          idListaVentaEstado: 2,
          nombre: '',
          vigenciaDesde: fechaHoy,
          vigenciaHasta: ''
        }
      })
    )
  }

  const handleInsertar = async () => {
    const nuevaLista = {
      idVendedor: values.idVendedor,
      idListaVentaEstado: values.idListaVentaEstado,
      nombre: values.nombre,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      ofertaDesde: values.ofertaDesde || null,
      ofertaHasta: values.ofertaHasta || null,
      porcDcto: values.porcDcto ? Number(values.porcDcto) : null
    }

    await insertarLista(nuevaLista)
    dispatch(setModal({ show: false }))
    setActualizar((prev) => !prev)
    toast.success('Lista creada correctamente.')
  }

  const handleActualizar = async () => {
    const listaActualizada = {
      idListaVenta: values.idListaVenta,
      idVendedor: values.idVendedor,
      idListaVentaEstado: values.idListaVentaEstado,
      nombre: values.nombre,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      ofertaDesde: values.ofertaDesde || null,
      ofertaHasta: values.ofertaHasta || null,
      porcDcto: values.porcDcto ? Number(values.porcDcto) : null
    }

    await actualizarLista(listaActualizada)
    dispatch(setModal({ show: false }))
    setActualizar((prev) => !prev)
    toast.success('Lista actualizada correctamente.')
  }

  useEffect(() => {
    const indiceUltimoItem = paginaActual * itemsPorPagina
    const indicePrimerItem = indiceUltimoItem - itemsPorPagina
    setListaVentasActual(listaFiltradaOrdenada.slice(indicePrimerItem, indiceUltimoItem))
  }, [listaFiltradaOrdenada, paginaActual, itemsPorPagina])

  const totalPaginas = Math.ceil(listaFiltradaOrdenada.length / itemsPorPagina)

  return (
    <ListaContenido
      containerClassName={styles.listaVentaContainer}
      topRef={topRef}
      titulo="Listas Venta"
      busqueda={busqueda}
      setBusqueda={setBusqueda}
      onBusqueda={(valor) => {
        handleBusqueda(valor)
        setPaginaActual(1)
      }}
      onOrdenarAZ={(dir) => {
        handleOrdenarAZ(dir)
        setPaginaActual(1)
      }}
      onOrdenarPorVencimiento={() => {
        handleOrdenarPorVencimiento()
        setPaginaActual(1)
      }}
      onAgregarLista={abrirModal}
      labelAgregar="Agregar lista"
      items={listaVentasActual}
      renderItem={(item, index) => (
        <div className="col-12 col-md-4 mb-3" key={`${item.idListaVenta ?? item.idLista ?? index}`}>
          <ItemLista
            lista={item}
            esVenta={true}
            onEditar={() => editarLista(item)}
            onVerItems={handleVerItems}
          />
        </div>
      )}
      onConfirmModal={handleConfirmModal}
      onChangeModal={handleChange}
      modalExtra={
        <ModalEditarItemsLista
          show={showModalItems}
          onHide={() => setShowModalItems(false)}
          listaVenta={listaSeleccionada}
          idVendedor={idVendedor}
          onActualizar={handleActualizarItems}
        />
      }
      totalPaginas={totalPaginas}
      paginaActual={paginaActual}
      onCambioPagina={(num) => {
        setPaginaActual(num)
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
      itemsPorPagina={itemsPorPagina}
      onCambioItemsPorPagina={(num) => {
        setItemsPorPagina(num)
        setPaginaActual(1)
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
    />
  )
}

export default ListaVenta
